import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, User, AUTH_EXPIRED_EVENT } from '../utils/api';
import { trackWebsiteTrafficConversion } from '../utils/gtag';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  signup: (data: {
    email: string;
    password: string;
    name: string;
    hospital?: string;
    specialization?: string;
  }) => Promise<{ userId: number }>;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 초기 로드 시 토큰 검증
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // 토큰 검증
          console.debug('[Auth] verifying stored token');
          const response = await authAPI.verifyToken();
          console.debug('[Auth] verify response:', response.data);
          if (response.data.valid) {
            // 검증 중 토큰이 갱신됐을 수 있다 — 저장소의 최신 값을 쓴다
            setToken(localStorage.getItem('token') || storedToken);
            setUser(JSON.parse(storedUser));
          } else {
            // 토큰이 무효한 경우 로컬 스토리지 정리
            console.warn('[Auth] token invalid; clearing storage');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error: any) {
          const status = error?.response?.status;
          if (status === 400 || status === 401 || status === 403) {
            // 세션이 정말 끝났다(갱신까지 실패) — 인터셉터가 저장소를 이미 비웠다
            console.warn('[Auth] session expired');
          } else {
            // 네트워크 오류·서버 재시작 중(5xx): 로그아웃하지 않고 저장된 세션을 유지한다
            console.warn('[Auth] verify failed temporarily; keeping stored session', status);
            setToken(storedToken);
            try {
              setUser(JSON.parse(storedUser));
            } catch {
              // 손상된 사용자 정보
            }
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // 사용 중 세션이 끝나면(갱신 토큰 만료·폐기) 페이지를 옮기지 않고 게스트 상태로 전환한다
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setToken(null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.debug('[Auth] login request:', { email });
      const response = await authAPI.login({ email, password });
      console.debug('[Auth] login response:', response.data);
      const { token: newToken, refreshToken, user: userData } = response.data;

      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Refresh Token 저장 (30일, 사용할 때마다 연장)
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
        console.debug('[Auth] Refresh token saved');
      }
    } catch (error: any) {
      console.error('[Auth] login error:', error?.response?.data || error?.message || error);
      throw new Error(error.response?.data?.error || '로그인에 실패했습니다.');
    }
  };

  const signup = async (data: {
    email: string;
    password: string;
    name: string;
    hospital?: string;
    specialization?: string;
  }): Promise<{ userId: number }> => {
    try {
      const response = await authAPI.signup(data);
      // 회원가입 성공 후 자동 로그인
      await login(data.email, data.password);
      
      // Google Ads 전환 추적
      trackWebsiteTrafficConversion({
        event_category: 'signup',
        event_label: 'user_registration',
      });
      
      return { userId: response.data.userId };
    } catch (error: any) {
      throw new Error(error.response?.data?.error || '회원가입에 실패했습니다.');
    }
  };

  const logout = async () => {
    // 서버에 로그아웃 요청 (refresh token 무효화)
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        await authAPI.logout(refreshToken);
        console.debug('[Auth] Logout successful');
      } catch (error) {
        console.error('[Auth] Logout error:', error);
        // 에러가 나도 로컬 로그아웃은 진행
      }
    }

    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    signup,
    loading,
    isAuthenticated: !!user && !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
